'use client'

import { Link } from '@tanstack/react-router'
import { usePathname } from '@/hooks/usePathname'
import { useTranslations } from '@/hooks/useTranslations'
import { ChevronRight } from 'lucide-react'
import { ROUTES, getRouteLabel } from '@/config/routes'
import { isMarketingPath } from '@/lib/route-gates'
import { useAuth } from './AuthProvider'

interface BreadcrumbItem {
  label: string
  href: string
}

// UUID regex pattern (standard UUID format)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function Breadcrumbs() {
  const { user } = useAuth()
  const pathname = usePathname()
  const tNav = useTranslations('nav')

  // Marketing/auth surfaces (login, setup-password, legal, …) skip the authed
  // chrome — same gate as the navbar. A cached `user` on a guard-bounced
  // /login would otherwise render an orphaned "home > Login" crumb.
  if (!user || isMarketingPath(pathname)) return null

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    // Skip breadcrumbs on home page
    if (pathname === ROUTES.HOME) {
      return []
    }

    const paths = pathname.split('/').filter(Boolean)
    // No leading "home" crumb (client feedback). The old home icon linked to
    // /dashboard — which is the Statistiken page, not a real home — so it was
    // misleading. The trail now starts at the first real path segment.
    const breadcrumbs: BreadcrumbItem[] = []

    let currentPath = ''
    paths.forEach((path) => {
      currentPath += `/${path}`

      // Check if path segment is a UUID - use generic "Details" label
      if (UUID_REGEX.test(path)) {
        breadcrumbs.push({
          label: tNav('details'),
          href: currentPath
        })
        return
      }

      // Get translation key and translate it
      const translationKey = getRouteLabel(path)
      const label = tNav(translationKey as Parameters<typeof tNav>[0]) || translationKey

      breadcrumbs.push({
        label,
        href: currentPath
      })
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  // Only show a trail (2+ levels); a lone crumb just duplicates the page title.
  if (breadcrumbs.length <= 1) {
    return null
  }

  return (
    <nav className="hidden md:flex items-center space-x-2 text-sm text-gray-600 mb-4">
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1

        return (
          <div key={`${index}:${crumb.href}`} className="flex items-center space-x-2">
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            {isLast ? (
              <span className="font-medium text-gray-900">{crumb.label}</span>
            ) : (
              <Link
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic breadcrumb hrefs aren't statically known to TanStack Router
                to={crumb.href as any}
                className="hover:text-blue-600 transition-colors"
              >
                <span>{crumb.label}</span>
              </Link>
            )}
          </div>
        )
      })}
    </nav>
  )
}
